'use client'

import NextLink from "@/components/NextLink";
import Button from '@/components/ui/Button';
import ModalInMobile from "@/components/ui/ModalInMobile";
import { currencies } from "@/constants/currencies";
import useHotelDetailStore from "@/core/stores/hotelDetail";
import { useRoomsStore } from "@/core/stores/rooms";
import { useUserStore } from '@/core/stores/user';
import useProvider1Store from '@/core/stores/provider1';
import useNextRouter from '@/hooks/useNextRouter';
import useSetSearchParams from "@/hooks/useSetSearchParams";
import useToast from '@/hooks/useToast';
import { formatDate } from '@/utils/dateOperators/formatDate';
import { deCodeOccupancies } from '@/utils/deCodeOccupancies';
import getNextDate from "@/utils/getNextDay";
import { slugifyOptions } from '@/utils/slugifyOptions';
import { faShield } from '@fortawesome/pro-regular-svg-icons/faShield';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Cookies from 'js-cookie';
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import slugify from 'slugify';
;


interface Occupancies {
    roomTypeId: string;
    adults: number;
    children: number;
    passengersDetails?: {
        firstName: string;
        lastName: string;
        gender: string;
    }[];
}

export default function HotelBill({ hotelId, hotelName, country, city, lat, lng, className }: { country: string, hotelId: string; hotelName: string; city: string; lat: number; lng: number; className?: string }) {

    const pathname = usePathname();
    const { roomsCount, setOrderId } = useRoomsStore();
    const { minPrice } = useHotelDetailStore()

    const { selectedRooms, mapRoomIds, setOrderStatus, hotelCode, addRoomsSpecials } = useProvider1Store()
    const { push } = useNextRouter();

    const totalPrice = selectedRooms.reduce((total, { selectedRate: { price } }) => {
        return total + parseFloat(price);
    }, 0).toFixed(2);


    const { params, getSearchParamsString } = useSetSearchParams();
    const { checkIn = getNextDate(0), checkOut = getNextDate(1), passenger = 'a1c-1' } = params;
    const [showCheckout, setShowCheckout] = useState(false)
    const [showDetails, setShowDetails] = useState(false)
    const [loading, setLoading] = useState(false)

    const { errorToast } = useToast()

    let roomsParam = "";

    selectedRooms.map((room) => {
        roomsParam += room.roomTypeId + ",";
    });

    const handleShowDetails = () => {
        setShowDetails(true)
    }

    const { setTotalAfterDiscount, setDiscountPrice } = useRoomsStore()

    const handlePush = async () => {
        const decodePassengers = deCodeOccupancies(passenger || "a1_c-1")?.map(
            (room) => {
                const occupancy: any = {
                    rooms: 1,
                    adults: room.adults,
                    children: room.children.length,
                };
                if (room.children.length) {
                    occupancy.childrenAge = room.children;
                }
                return occupancy;
            },
        );


        if (selectedRooms.length !== decodePassengers.length) {
            errorToast(`You have searched for ${decodePassengers.length} rooms but currently selected ${selectedRooms.length}`)
            return
        }

        const occupancies: Occupancies[] = selectedRooms
            .map((item, index) => {
                if (item.occupancies) {
                    const occupyObject: any = {
                        ...item.occupancies,
                        adults: decodePassengers[index].adults,
                        children: decodePassengers[index].children,
                        rateBasisId: item.selectedRate.rateBasisId,
                        allocationDetails: item.selectedRate.allocationDetails,
                    };

                    if (decodePassengers[index].children) {
                        occupyObject.childrenAge = decodePassengers[index].childrenAge;
                    }

                    if ('passengersDetails' in occupyObject) {
                        delete occupyObject.passengersDetails;
                    }

                    return occupyObject;
                } else {
                    return null;
                }
            })
            .filter((occupancy): occupancy is NonNullable<typeof occupancy> => Boolean(occupancy));

        setLoading(true)

        try {
            const response = await fetch('/api/orders/pre-book', {
                method: 'POST',
                body: JSON.stringify({
                    code: hotelCode,
                    occupancies,
                    stay: {
                        checkIn,
                        checkOut
                    }
                })
            })

            const { result } = await response.json()

            if (response.ok && result.status === 'successful') {
                setOrderId(result.orderId)
                mapRoomIds(result.room,)
                setTotalAfterDiscount(0)
                setDiscountPrice(0)
                result.room.map((room: any) => {
                    if (room.specialRequests) {
                        addRoomsSpecials(room.roomId, room.specialRequests)
                    }
                })
                push(`/checkout?city=${city}&orderId=${result.orderId}&hotelCode=${hotelCode}&checkIn=${checkIn || getNextDate(7)}&checkOut=${checkOut || getNextDate(14)}&hotelId=${hotelId}&rooms=${roomsParam}&passenger=${passenger || "a1_c-1"}&lat=${lat}&lng=${lng}`);
            } else if (response.ok && result.status === "unsuccessful") {

                result.room.map((item: any) => {
                    if (item.status === "unsuccessful") {
                        setOrderStatus(item.index, "unsuccessful")
                        errorToast(`${item.roomName} is not currently available.`)
                    }
                })

            } else {
                errorToast(result.message || result.error || 'Booking failed!')
            }


        } catch (err) {

            errorToast('Booking Failed')
        }
        setLoading(false)



    };

    useEffect(() => {
        if (selectedRooms.length > 0) {
            setShowCheckout(true)
        }

        return () => {
            setShowCheckout(false)
        }
    }, [roomsCount])
    const currency = Cookies.get('currency') || 'USD'
    const { addActions } = useUserStore()

    return (
        <>
            {(!pathname.includes(`/rooms`) || selectedRooms.length !== 0) &&
                <div
                    className={`${className} flex md:hidden flex-row justify-between items-center fixed
                     bottom-0 left-0 px-4 py-3 z-[120] bg-white w-full shadow-card-top-sm`
                    }
                >
                    <div className='flex flex-col justify-between h-full '>
                        {selectedRooms.length === 0 && <span className="text-[10px] text-gray-500 pr-2 font-normal">From</span>}
                        <div className="flex flex-row justify-between text-lg text-secondary-500">
                            <p>
                                {currencies[currency]}
                                {Number(totalPrice) === 0 ? minPrice : totalPrice}
                            </p>
                        </div>
                        {/* <span className='text-xs text-gray-500'>{selectedRooms.length === 0 ? availableRooms : selectedRooms.length} Room {allPassengers > 0 ? `for` + ' ' + allPassengers + ` Guest` : ''}</span> */}
                    </div>
                    <div className="w-fit">
                        {selectedRooms.length === 0 ? <NextLink
                            onClick={() => { addActions({ type: 'clicked', message: 'opened more room page' }) }}
                            href={`/hotel/${slugify(country, slugifyOptions)}/${slugify(city, slugifyOptions)}/${slugify(hotelName, slugifyOptions)}/rooms?${getSearchParamsString()}`}
                            className={`w-full min-w-full rounded-full bg-main-500 px-5 py-2.5 text-white text-xs`} >
                            Select Room
                        </NextLink> : <Button radius='rounded-full'
                            color='primary' onClick={handleShowDetails}
                            className={`w-full min-w-full`} >
                            Book now
                        </Button>}
                        <ModalInMobile
                            zIndex={6000}
                            title="You selected"
                            onClose={() => setShowDetails(false)}
                            show={showDetails}>
                            <div className='pb-4 h-[320px] '>
                                <div className="flex w-full h-[200px]  scroll-mx-1.5 overflow-y-auto flex-col gap-4 pb-3">
                                    {selectedRooms.map((room, index) => (
                                        <div key={index + 'room-card-bill-mobile'} className={` rounded-2xl border-2 border-gray-200 flex flex-row items-start justify-between gap-2 p-4`} >
                                            <div className="flex w-full flex-col text-sm font-semibold text-gray-700" key={index} >
                                                <p className="flex w-full items-center gap-1 ">
                                                    <span className="capitalize">{room.name.toLowerCase()}</span>
                                                </p>
                                                <div className="pt-2 text-xs font-normal flex flex-row items-center gap-2">
                                                    <FontAwesomeIcon icon={faUser} className="w-4 h-4" />
                                                    <p>
                                                        <span>Max Occupancy </span>
                                                        <span>{room.maxOccupancy} </span>
                                                    </p>
                                                </div>
                                                {(!room.selectedRate?.withinCancellationDeadline) ?
                                                    <div className='flex flex-col'>

                                                        <div className='grid grid-cols-[16px,_1fr] items-center gap-1 my-2'>
                                                            <FontAwesomeIcon className='text-green-500 ' icon={faShield} />
                                                            <div className='flex flex-col text-neutral-700 text-sm font-normal'>
                                                                <span className='text-green-500 font-normal text'>Refundable</span>

                                                            </div>

                                                        </div>
                                                        <div className='flex flex-col gap-1 text-xs font-normal text-gray-700'>
                                                            {room.selectedRate.cancellationRules.map(item => (<div key={item.fromDate}>
                                                                Cancellation {item.fromDate ? `from ${formatDate(item.fromDate!, true)}` : ''} {item.toDate ? `until ${formatDate(item.toDate!, true)}` : ''} will charge <span className='text-rose-500'> {currencies[currency]}{item.charge}</span>
                                                            </div>))}
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className='grid grid-cols-[16px,_1fr] items-center gap-1 my-2'>
                                                        <FontAwesomeIcon className='text-rose-500 ' icon={faShield} />
                                                        <div className='flex flex-col text-neutral-700 text-sm font-normal'>
                                                            <span className='text-rose-500 font-normal text'>Not Refundable</span>

                                                        </div>

                                                    </div>

                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-row justify-between border-t border-dashed border-t-gray-300 pt-2 pb-4  font-semibold text-gray-900">
                                    <p>Total</p>
                                    <p>
                                        {currencies[currency]}
                                        {Number(totalPrice) ? totalPrice : '0.00'}
                                    </p>
                                </div>
                                <Button color='primary' radius='rounded-full' onClick={handlePush}
                                    isLoading={loading}
                                    disabled={selectedRooms.length === 0 ? true : false || loading}
                                    className={`   w-full`} >
                                    Complete Booking
                                </Button>
                            </div>
                        </ModalInMobile>
                    </div>
                </div>}
        </>
    )
}